/**
 * "Get your shop ready" (T11-47, design/04 §0 "ตัวช่วยในหน้า"): the nine first-day steps and
 * whether each is done — always from what the services say (never a tick stored in the browser).
 * There is no `GET /admin/onboarding/status` (gap, T11-51 → T01); the admin composes it from the
 * settings endpoints it already reads. Pure and unit tested; the queries are in
 * `useOnboardingStatus.ts`.
 *
 * | step | done when | source |
 * |---|---|---|
 * | ① shop details | shop name (ja), address, phone and e-mail are filled | cms 特商法 / 古物 / contact drafts (T11-44) |
 * | ② 特商法表記 | all 14 items have Japanese text (100 %) | cms `tokushoho` policy |
 * | ③ tax | a real T-number (not the seed placeholder) and a saved business type | tax `GET /admin/settings/tax` |
 * | ④ ways to pay | an active bank transfer with a complete account (not the seed `0000000`) and an active card terminal | payments `GET /admin/payment-methods` |
 * | ⑤ shipping | an active zone with Japan and an active zone abroad, both with ≥ 1 way to ship | orders `GET /admin/shipping-zones` |
 * | ⑥ register | an active register with ≥ 1 paired tablet | pos `GET /admin/pos/registers` |
 * | ⑦ first card | the catalog has ≥ 1 product | catalog `GET /admin/products?limit=1` |
 * | ⑧ staff | ≥ 2 active staff accounts | identity `GET /admin/staff` |
 * | ⑨ two-step sign-in | the signed-in owner has TOTP on | identity (staff row, else the session) |
 */
import { completeness, type PolicyFields } from "@/features/settings/tokushoho/items";
import { factsFrom } from "@/features/settings/storeProfileModel";

export const CHECKLIST_KEYS = [
  "store",
  "tokushoho",
  "tax",
  "payments",
  "shipping",
  "register",
  "first_card",
  "staff",
  "two_step",
] as const;
export type ChecklistKey = (typeof CHECKLIST_KEYS)[number];

export type StepStatus = "done" | "todo" | "unknown" | "no_access";

/** Where each step is set up (a screen of the admin). */
export const CHECKLIST_ROUTES: Record<ChecklistKey, string> = {
  store: "/settings/store",
  tokushoho: "/content/policies/tokushoho",
  tax: "/settings/tax",
  payments: "/settings/payments",
  shipping: "/settings/shipping",
  register: "/pos/registers",
  first_card: "/products/new",
  staff: "/settings/staff",
  two_step: "/profile",
};

/** One source's answer: loading, failed, not allowed for this person, or the data. */
export type Source<T> =
  { state: "loading" } | { state: "error" } | { state: "no_access" } | { state: "ok"; data: T };

export interface ChecklistInputs {
  policies: Source<{ tokushoho: PolicyFields; kobutsu: PolicyFields; contact: PolicyFields }>;
  tax: Source<{
    invoiceRegistrationNumber: string | null;
    registrationNumberIsPlaceholder: boolean;
    businessType: string | null;
  }>;
  methods: Source<
    ReadonlyArray<{
      method: string;
      provider?: string;
      active: boolean;
      instructions?: { bankAccount?: { accountNumber?: string | null } | null } | null;
    }>
  >;
  zones: Source<
    ReadonlyArray<{ active: boolean; countries: readonly string[]; rateCount: number }>
  >;
  registers: Source<ReadonlyArray<{ status: string; deviceCount: number }>>;
  products: Source<{ count: number }>;
  staff: Source<ReadonlyArray<{ id: string; active: boolean; totpEnabled: boolean }>>;
  /** The signed-in person (their own two-step sign-in is always known). */
  me: { id: string; totpEnabled: boolean | undefined };
}

export interface ChecklistStep {
  key: ChecklistKey;
  /** ① … ⑨ */
  no: number;
  status: StepStatus;
  to: string;
  /** ② only: how full the 特商法 page is. */
  percent?: number;
}

/** The seed's bank account placeholder (infra/seed demo) is not a real account. */
const PLACEHOLDER_ACCOUNT = /^0+$/;

function fromSource<T>(source: Source<T>, done: (data: T) => boolean): StepStatus {
  switch (source.state) {
    case "ok":
      return done(source.data) ? "done" : "todo";
    case "no_access":
      return "no_access";
    default:
      return "unknown";
  }
}

export function storeDone(policies: {
  tokushoho: PolicyFields;
  kobutsu: PolicyFields;
  contact: PolicyFields;
}): boolean {
  const facts = factsFrom(policies.tokushoho, policies.kobutsu, policies.contact);
  return [facts.shopName.ja, facts.address.ja, facts.phone, facts.email].every(
    (value) => value.trim() !== "",
  );
}

export function checklistSteps(inputs: ChecklistInputs): ChecklistStep[] {
  const percent =
    inputs.policies.state === "ok" ? completeness(inputs.policies.data.tokushoho).percent : 0;
  const status: Record<ChecklistKey, StepStatus> = {
    store: fromSource(inputs.policies, storeDone),
    tokushoho: fromSource(inputs.policies, () => percent === 100),
    tax: fromSource(
      inputs.tax,
      (tax) =>
        Boolean(tax.invoiceRegistrationNumber?.trim()) &&
        !tax.registrationNumberIsPlaceholder &&
        tax.businessType !== null,
    ),
    payments: fromSource(inputs.methods, (methods) => {
      const bank = methods.some((m) => {
        const account = m.instructions?.bankAccount?.accountNumber?.trim() ?? "";
        return (
          (m.method === "bank_transfer" || m.provider === "bank_transfer_manual") &&
          m.active &&
          account !== "" &&
          !PLACEHOLDER_ACCOUNT.test(account)
        );
      });
      const terminal = methods.some(
        (m) => (m.method === "edc_card" || m.provider === "edc_external") && m.active,
      );
      return bank && terminal;
    }),
    shipping: fromSource(inputs.zones, (zones) => {
      const live = zones.filter((z) => z.active && z.rateCount > 0);
      return (
        live.some((z) => z.countries.includes("JP")) &&
        live.some((z) => z.countries.some((c) => c !== "JP"))
      );
    }),
    register: fromSource(inputs.registers, (registers) =>
      registers.some((r) => r.status === "active" && r.deviceCount > 0),
    ),
    first_card: fromSource(inputs.products, (products) => products.count > 0),
    staff: fromSource(inputs.staff, (staff) => staff.filter((s) => s.active).length >= 2),
    two_step: (() => {
      const own =
        inputs.staff.state === "ok"
          ? inputs.staff.data.find((s) => s.id === inputs.me.id)
          : undefined;
      const on = own?.totpEnabled ?? inputs.me.totpEnabled;
      return on === undefined ? "unknown" : on ? "done" : "todo";
    })(),
  };
  return CHECKLIST_KEYS.map((key, index) => ({
    key,
    no: index + 1,
    status: status[key],
    to: CHECKLIST_ROUTES[key],
    ...(key === "tokushoho" ? { percent } : {}),
  }));
}

export function progress(steps: readonly ChecklistStep[]): { done: number; total: number } {
  return { done: steps.filter((s) => s.status === "done").length, total: steps.length };
}
